import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Formik, FormikHelpers } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import styled, { css, up } from "@xstyled/styled-components";

import {
  oldTransformIn,
  transformErrors,
  transformIn,
  transformOut,
  oldTransformOut,
} from "./transformers";
import { JobFormFields, OldJobFormFields } from "./types";
import {
  formatOptions,
  showTechnologiesUsedByJob,
  validationSchema,
  oldValidationSchema,
} from "./util";
import Sections, * as Section from "./NewSections";
import { Preview } from "./Preview";

import { handleMutationError } from "@hire/errors";
import {
  CompanyDetailsDocument,
  CompanyWorkflowStatus,
  JobCategorizationDataDocument,
  JobFragment,
  JobWorkflowStatus,
  ProfileType,
  UpdateViewSalaryPermissionDocument,
  UpsertCompanyJobDocument,
} from "@hire/schema";
import { Loading } from "@otta/shared-components";
import {
  Button,
  Card,
  ErrorContainer,
  ErrorText,
  Modal,
  Spacing,
  Text,
} from "@otta/design";
import { useExperiment } from "@otta/experiments/client";
import { Experiment } from "@hire/constants/experiments";
import { palette } from "@otta/design-tokens";
import { pushAnalyticsEvent } from "@otta/analytics";

/**
 * Just cheat and put a loading spinner behind the iframe
 * so it'll show up until the webpage does
 */
const LoadingWrapper = styled.div`
  display: flex;
  inset: 0 0 0 0;
  justify-content: center;
  position: absolute;
  z-index: -1;
`;

const JobFrame = styled.iframe`
  border: none;
  flex-basis: 100%;
  flex-grow: 1;
  overflow-x: hidden;
`;

const JobModal = styled(Modal)`
  border: 1px solid ${palette.grayscale.shade400};
  box-shadow: 0 0 1rem ${palette.grayscale.shade400};
  min-height: 80vh;
  min-width: 60vw;

  & > [aria-label="close"] {
    display: none;
  }

  & > div {
    flex-direction: column;
    display: flex;
  }
`;

const JobModalBar = styled.div`
  box-shadow: 0rem 0.25rem 1.25rem 0rem rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: auto;
  padding: lg;
  z-index: 1;
`;

const StickyBottomBar = styled.div`
  position: fixed;
  inset: auto 0 0 0;
  width: 100%;
  background-color: white;
  box-shadow: 0rem 0.25rem 1.25rem 0rem rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: md;
  align-items: center;
  gap: lg;
`;

const PageError = styled(ErrorContainer)`
  justify-content: center;
  margin: md;
`;

const StyledCard = styled(Card)`
  padding: xl;
  margin-bottom: 7.5rem;
`;

const SplitPage = styled.div`
  flex-direction: row;
  position: relative;
  display: flex;
  gap: 2rem;
`;

const PreviewPane = styled.div`
  background-color: beige-200;
  position: relative;
  display: none;

  ${up(
    "desktop",
    css`
      display: block;
    `
  )}
`;

const PreviewPaneInner = styled.div`
  top: 20;
  position: sticky;
`;

export const JobEditForm = ({
  companyJob,
  onSubmitRedirect,
}: {
  companyJob?: JobFragment | null;
  onSubmitRedirect?: string | undefined;
}) => {
  const navigate = useNavigate();
  const { companySlug } = useParams();
  const [params] = useSearchParams();
  const [showPreview, setShowPreview] = useState(!!params.get("preview"));

  const { variant } = useExperiment(Experiment.NewLocation);
  const hasNewLocation = variant === "variant";
  const { data, loading } = useQuery(JobCategorizationDataDocument);
  const { data: companyData } = useQuery(CompanyDetailsDocument);
  const companyWorkflowStatus =
    companyData?.currentUser?.currentCompany?.workflowStatus;

  const isCompanyStatusCompleted =
    companyWorkflowStatus &&
    (companyWorkflowStatus === CompanyWorkflowStatus.Completed ||
      companyWorkflowStatus === CompanyWorkflowStatus.Changed ||
      companyWorkflowStatus === CompanyWorkflowStatus.CompletedByUser);

  const showEBContent =
    companyData?.currentUser?.currentCompany?.profileTypeName ===
    ProfileType.Enhanced;

  const [upsertJobMutation] = useMutation(UpsertCompanyJobDocument);

  const [updateSalaryPermissions] = useMutation(
    UpdateViewSalaryPermissionDocument,
    {
      onError: handleMutationError,
    }
  );

  const isEditingJob = !!companyJob;
  const isDraftJob =
    !isEditingJob ||
    companyJob?.workflowStatus === JobWorkflowStatus.RecruiterDraft;

  const initialValues = useMemo(
    () =>
      hasNewLocation ? transformIn(companyJob) : oldTransformIn(companyJob),
    [companyJob, hasNewLocation]
  );

  const hasAvailableSlots =
    (companyData?.currentUser?.currentCompany?.jobSlotData?.available ?? 0) > 0;

  const handleSubmit = useCallback(
    (
      data: JobFormFields | OldJobFormFields,
      form: FormikHelpers<JobFormFields | OldJobFormFields>
    ) => {
      const formattedData = hasNewLocation
        ? transformOut(data as JobFormFields)
        : oldTransformOut(data as OldJobFormFields);

      return upsertJobMutation({
        variables: {
          input: formattedData,
          id: data?.externalId ?? null,
          isPreview: data.isPreview ?? false,
        },
        onCompleted: async mutationData => {
          const upsertedJob = mutationData.upsertCompanyJob;
          // The only time we will not navigate will be if there is an error
          if (!upsertedJob) {
            return;
          }
          const upsertedJobId = upsertedJob.externalId;

          if (data.salaryVisibleToIds.length) {
            await updateSalaryPermissions({
              variables: {
                userIds: data.salaryVisibleToIds,
                jobId: upsertedJobId,
              },
            });
          }

          if (data.isPreview) {
            form.setFieldValue("externalId", upsertedJobId);
            setShowPreview(true);
            return;
          }

          if (
            upsertedJob.workflowStatus === JobWorkflowStatus.CompletedByUser &&
            !hasAvailableSlots &&
            !isEditingJob
          ) {
            navigate(`/${companySlug}/jobs/pricing/${upsertedJobId}`);
            return;
          }

          if (onSubmitRedirect) {
            navigate(onSubmitRedirect);
            return;
          }

          navigate(`/${companySlug}/jobs/${upsertedJobId}`);
        },
        onError: (error: ApolloError) => {
          if (error.graphQLErrors.length > 0) {
            error.graphQLErrors.forEach(({ message }: { message: string }) => {
              const errorMessages = message.split(", ");
              errorMessages.forEach(errorMsg => {
                const [field, ...rest] = errorMsg.split(" ");
                const { field: transformedField, displayName } =
                  transformErrors()[field] || {};
                const errorMessage = `Your ${displayName} ${rest.join(" ")}`;
                if (transformedField && errorMessage) {
                  form.setFieldError(transformedField, errorMessage);
                }
              });
            });
          } else {
            handleMutationError(error);
          }
        },
      });
    },
    [
      companySlug,
      hasAvailableSlots,
      hasNewLocation,
      isEditingJob,
      navigate,
      onSubmitRedirect,
      updateSalaryPermissions,
      upsertJobMutation,
    ]
  );

  const submitCopy = useMemo(() => {
    if (companyJob) {
      return "Publish changes";
    }
    return "Publish job";
  }, [companyJob]);

  const { pathname } = useLocation();

  useEffect(() => {
    if (companyData && !showEBContent) {
      pushAnalyticsEvent({
        eventName: "Company Recruiter Viewed Upsell",
        name: "job-team-upsell-find-out-more",
        pathname: pathname,
      });
    }
  }, [pathname, showEBContent, companyData]);

  if (loading || !data) {
    return <Loading />;
  }

  const {
    jobFunctionOptions,
    technologiesUsedOptions,
    languageRequirementOptions,
  } = formatOptions(data);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={hasNewLocation ? validationSchema : oldValidationSchema}
      validateOnChange={false}
      validateOnBlur={true}
      onSubmit={(data, form) => {
        return handleSubmit(data, form);
      }}
    >
      {form => (
        <SplitPage>
          <StyledCard>
            <JobModal
              open={showPreview}
              scrollable={false}
              onOpenChange={v => setShowPreview(v)}
            >
              <LoadingWrapper>
                <Loading />
              </LoadingWrapper>
              <JobFrame
                title="Job preview"
                src={`${import.meta.env.VITE_SEARCH_APP_HOST}/jobs/${
                  form.values.externalId
                }?if=1`}
              />
              <JobModalBar>
                <Button
                  level="primary"
                  onClick={() => {
                    form.setFieldValue("isPreview", false, false);
                    form.submitForm();
                  }}
                >
                  Publish changes
                </Button>
                <Button
                  level="secondary"
                  onClick={() => {
                    form.setFieldValue("isPreview", false, false);
                    setShowPreview(false);
                  }}
                >
                  Close
                </Button>
              </JobModalBar>
            </JobModal>
            <form onSubmit={form.handleSubmit}>
              <Sections>
                <Section.Titles />
                <Section.Compensation companyJob={companyJob} />
                <Section.Functions
                  jobFunctionOptions={jobFunctionOptions}
                  companyJob={companyJob}
                />
                {showEBContent && (
                  <Section.Team
                    teamOptions={
                      companyData?.currentUser?.currentCompany?.teams.map(
                        team => ({
                          value: team.id,
                          label: team.name,
                        })
                      ) || []
                    }
                    companyJob={companyJob}
                  />
                )}
                {!showEBContent && (
                  <Section.TeamUpsell
                    isProCompany={
                      !!companyData?.currentUser?.currentCompany?.isPro
                    }
                  />
                )}
                <Section.Experience />
                {showTechnologiesUsedByJob(form.values.jobFunction) && (
                  <Section.Technologies
                    technologiesUsedOptions={technologiesUsedOptions}
                  />
                )}

                {hasNewLocation ? (
                  <Section.Location />
                ) : (
                  <Section.OldLocation />
                )}

                <Section.WhoYouAre
                  languageRequirementOptions={languageRequirementOptions}
                />
                <Section.JobInvolves />
                <Section.ApplicationProcess />
              </Sections>
              <StickyBottomBar>
                {Object.keys(form.errors).length > 0 &&
                  form.submitCount > 0 && (
                    <PageError>
                      <ErrorText>Enter missing information</ErrorText>
                    </PageError>
                  )}
                <SubmitButtonWrapper>
                  <Button
                    type="submit"
                    level="primary"
                    disabled={form.isSubmitting}
                  >
                    {submitCopy}
                  </Button>
                  {isDraftJob && isCompanyStatusCompleted && (
                    <Button
                      type="button"
                      level="secondary"
                      disabled={form.isSubmitting}
                      onClick={() => {
                        form.setFieldValue("isPreview", true, false);
                        form.submitForm();
                      }}
                    >
                      {form.isSubmitting
                        ? "Saving..."
                        : "Save draft and preview"}
                    </Button>
                  )}
                </SubmitButtonWrapper>
              </StickyBottomBar>
            </form>
          </StyledCard>
          <PreviewPane>
            <PreviewPaneInner>
              <Spacing size={3}>
                <Spacing size={0}>
                  <Text bold as={"h2"} size={1}>
                    Preview
                  </Text>
                </Spacing>
                <Preview />
              </Spacing>
            </PreviewPaneInner>
          </PreviewPane>
        </SplitPage>
      )}
    </Formik>
  );
};
